import { Check, Link as LinkIcon, X } from "@phosphor-icons/react";
import { type ChangeEvent, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { DomainMode, SearchEngine, SourcePolicy } from "~/api/governanceTypes";
import {
  useDecideSourceApproval,
  useSaveSourcePolicy,
  useSourceApprovals,
  useSourcePolicy,
} from "~/api/queries";
import { Select, TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { IconButton } from "~/foundation/components/IconButton";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";

/** Label · description · control, three lines of Tailwind — kms's `Settings/components/SettingRow`. */
function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-divider py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-body text-text">{label}</p>
        {description ? <p className="text-small text-text-tertiary">{description}</p> : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function DomainsEditor({ policy }: { policy: SourcePolicy }) {
  const { t } = useTranslation();
  const savePolicy = useSaveSourcePolicy();
  const [draft, setDraft] = useState(policy.domains.join("\n"));

  // Re-sync only when the server's own domain list changes value — typing must never be
  // clobbered by the query's background refetch re-running this effect for no real reason.
  const domainsKey = policy.domains.join("\n");
  useEffect(() => {
    setDraft(domainsKey);
  }, [domainsKey]);

  const save = () => {
    const domains = draft
      .split(/[\n,]/)
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d.length > 0);
    savePolicy.mutate(
      { ...policy, domains },
      { onSuccess: () => toast.success(t("Domains saved")) },
    );
  };

  return (
    <div className="flex flex-col gap-2 border-b border-divider py-3 last:border-b-0">
      <TextArea
        label={t("Domains")}
        hint={t('One per line, or comma-separated. "*.gov" matches subdomains.')}
        rows={4}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <div className="flex justify-end">
        <Button size="sm" variant="neutral" disabled={savePolicy.isPending} onClick={save}>
          {t("Save domains")}
        </Button>
      </div>
    </div>
  );
}

export function WebSourcesSection() {
  const { t } = useTranslation();
  const { data: policy, isLoading } = useSourcePolicy();
  const savePolicy = useSaveSourcePolicy();
  const { data: approvals } = useSourceApprovals("pending");
  const decide = useDecideSourceApproval();

  if (isLoading || !policy) {
    return <Spinner label={t("Loading")} />;
  }

  const update = (patch: Partial<SourcePolicy>) => {
    savePolicy.mutate({ ...policy, ...patch });
  };

  const pending = approvals ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border border-divider px-3">
        <SettingRow
          label={t("Default search engine")}
          description={t(
            "Which engine a thinker's web search uses when it does not ask for one itself.",
          )}
        >
          <Select
            value={policy.engineDefault}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              update({ engineDefault: event.target.value as SearchEngine })
            }
          >
            <option value="duckduckgo">DuckDuckGo</option>
            <option value="google">Google</option>
            <option value="bing">Bing</option>
          </Select>
        </SettingRow>

        <SettingRow
          label={t("Lock the engine")}
          description={t("Force the default above even when a thinker asks for a different one.")}
        >
          <Switch
            checked={policy.engineLocked}
            onCheckedChange={(checked) => update({ engineLocked: checked })}
            label={t("Lock the engine")}
          />
        </SettingRow>

        <SettingRow
          label={t("Domain rule")}
          description={t("Restrict which domains a thinker may read from.")}
        >
          <Select
            value={policy.domainMode}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              update({ domainMode: event.target.value as DomainMode })
            }
          >
            <option value="off">{t("No restriction")}</option>
            <option value="allow">{t("Allow only these domains")}</option>
            <option value="deny">{t("Block these domains")}</option>
          </Select>
        </SettingRow>

        {policy.domainMode !== "off" ? <DomainsEditor policy={policy} /> : null}

        <SettingRow
          label={t("Require approval for new sources")}
          description={t("(Enforcement coming soon.)")}
        >
          <Switch
            checked={policy.requireSourceApproval}
            onCheckedChange={(checked) => update({ requireSourceApproval: checked })}
            label={t("Require approval for new sources")}
          />
        </SettingRow>

        <SettingRow label={t("Max sources per run")} description={t("0 means no limit.")}>
          <NumberField
            value={policy.maxSourcesPerRun}
            onCommit={(value) => update({ maxSourcesPerRun: value })}
          />
        </SettingRow>

        <SettingRow label={t("Max crawl depth")} description={t("0 uses the tool's own default.")}>
          <NumberField
            value={policy.maxCrawlDepth}
            onCommit={(value) => update({ maxCrawlDepth: value })}
          />
        </SettingRow>
      </div>

      {policy.requireSourceApproval && pending.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Text variant="small" className="font-medium text-text-secondary">
            {t("Pending approvals")}
          </Text>
          <ul className="flex flex-col rounded-md border border-divider">
            {pending.map((approval) => (
              <li
                key={approval.id}
                className="flex items-center gap-3 border-b border-divider px-3 py-2.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">{approval.domain}</p>
                  <div className="flex items-center gap-2 text-small text-text-tertiary">
                    {approval.sampleUrl ? (
                      <a
                        href={approval.sampleUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-text"
                      >
                        <LinkIcon size={12} aria-hidden />
                        <span className="truncate">{approval.sampleUrl}</span>
                      </a>
                    ) : null}
                  </div>
                </div>
                <IconButton
                  icon={Check}
                  aria-label={t("Approve")}
                  onClick={() => decide.mutate({ id: approval.id, status: "approved" })}
                />
                <IconButton
                  icon={X}
                  aria-label={t("Reject")}
                  onClick={() => decide.mutate({ id: approval.id, status: "rejected" })}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Saves on blur, not on change — the two number fields in the wave spec's interaction rules. */
function NumberField({ value, onCommit }: { value: number; onCommit: (value: number) => void }) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => setDraft(String(value)), [value]);

  return (
    <input
      type="number"
      min={0}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        const next = Number.parseInt(draft, 10);
        onCommit(Number.isFinite(next) && next >= 0 ? next : value);
      }}
      className="h-8 w-24 rounded-sm border border-input-border bg-bg px-2 text-end text-body text-text focus:border-input-border-focus focus:outline-none"
    />
  );
}
